// apps/roleforge-app/src/lib/roleforge-invite-limits.ts
//
// Issue #1324 — stakeholder invite slots are an ORGANIZATION-wide, lifetime
// counter on the server (`organizations.preactivation_interview_count`), and
// revoking an invite does not return the slot. Both surfaces used to compute
// `used = invitations.length` for the CURRENT AUDIT, so the number the owner
// saw and the number the server enforced were unrelated: "1 left" on a request
// that 402s, "2 left" on a second audit where every invite is refused.
//
// The server now returns the authoritative figures on
// `GET /audits/:id/invitations`; everything below renders exactly what it said
// and never invents a count.

/**
 * Beta display fallback only — the enforced cap is the sponsor's
 * `interview_invite_cap` reported in `StakeholderInviteSlots.cap`. Migration
 * `20260620000001_roleforge_stakeholder_invite_cap.sql` aligns the two.
 */
export const ROLEFORGE_STAKEHOLDER_INVITE_CAP = 2;

/** Mirrors `StakeholderInviteSlots` in apps/server/src/services/roleforge-invite-slots.ts. */
export interface StakeholderInviteSlots {
  unlimited: boolean;
  reason:
    | 'subscribed'
    | 'activated'
    | 'preactivation_trial'
    | 'preactivation_expired'
    | 'no_preactivation_sponsor'
    | 'unknown';
  used: number;
  cap: number | null;
  remaining: number | null;
}

export interface InviteSlotDisplay {
  /** Headline usage line, e.g. "1 of 2 organization slots used". */
  usage: string;
  /** Short badge, e.g. "1 left" / "Unlimited" / "Unavailable". */
  remaining: string;
  /** Plain-language truth about how slots are consumed. Always rendered. */
  note: string;
  /** False when the server could not report slots at all. */
  known: boolean;
}

const CONSUMED_ON_SEND_NOTE =
  'Slots are counted across your whole organization and are used up when you send — revoking an invite does not return one.';

const UNKNOWN_DISPLAY: InviteSlotDisplay = {
  usage: 'Slot count unavailable',
  remaining: '—',
  note: 'We could not read your invite slots just now. Sending will tell you for certain.',
  known: false,
};

export function describeInviteSlots(
  slots: StakeholderInviteSlots | null | undefined,
): InviteSlotDisplay {
  // No slots block at all: a server older than #1324.
  if (!slots) return UNKNOWN_DISPLAY;

  // `reason: 'unknown'` is a READ FAILURE, not an answer about entitlement. The
  // server always sends a slots object, so without this branch a transient
  // database blip fell through to the cap===null copy and told a paying customer
  // their organization cannot invite.
  if (!slots.unlimited && slots.reason === 'unknown') return UNKNOWN_DISPLAY;

  if (slots.unlimited) {
    return {
      usage: 'Unlimited stakeholder invites',
      remaining: 'Unlimited',
      note:
        slots.reason === 'subscribed'
          ? 'Your plan includes unlimited stakeholder invites.'
          : 'Your activated trial includes unlimited stakeholder invites.',
      known: true,
    };
  }

  if (slots.reason === 'preactivation_expired') {
    return {
      usage: `${slots.used} used — free period ended`,
      remaining: '0 left',
      note: 'Your free pre-activation period has ended. Activate a plan to keep inviting stakeholders.',
      known: true,
    };
  }

  // No sponsorship (or no resolvable sponsor cap): the org genuinely has no free
  // invite slots. The server refuses these with 402 `invite_not_entitled`.
  if (slots.reason === 'no_preactivation_sponsor' || slots.cap === null) {
    return {
      usage: 'No free invitation slots',
      remaining: 'Needs a plan',
      note: 'Stakeholder invites are included with a VibeControl trial or plan. Activate one to invite your team.',
      known: true,
    };
  }

  const remaining = slots.remaining ?? Math.max(slots.cap - slots.used, 0);
  return {
    usage: `${slots.used} of ${slots.cap} organization slots used`,
    remaining: `${remaining} left`,
    note: CONSUMED_ON_SEND_NOTE,
    known: true,
  };
}
